import * as THREE from 'three';
import { ITexture } from '../../@types/ContextProviderRelated';
import { DefaultAttributeId, getDefaultObjectId, ObjectType, syncIdAttributeByUuid } from '../../utils/attributes/defaultObjectsAndAttributes';
import { DEFAULT_EMPTY_TEXTURE } from '../../utils/constants';
import { serializeTexture } from '../../utils/export/serializeTexture';
import Entity, { IEntity } from '../abstract/Entity';
import { IAttribute } from './AttributeModel';
import { ITile } from './TileModel';

export interface IFloor extends IEntity {
  tiles: ITile | undefined;
  texture?: ITexture | null;
  floorShape: THREE.Shape | undefined;
}

class FloorModel extends Entity implements IFloor {
  tiles: ITile | undefined;

  texture?: ITexture | null;

  floorShape: THREE.Shape | undefined;

  readonly filesApiId: number = getDefaultObjectId(ObjectType.Floor);

  constructor(tiles?: ITile, texture?: ITexture | null, attributes: Array<IAttribute> = [], uuid: string | undefined = undefined) {
    super(attributes);
    if (uuid) {
      this.uuid = uuid;
    }
    this.tiles = tiles;
    this.texture = texture ?? DEFAULT_EMPTY_TEXTURE;

    const synced = syncIdAttributeByUuid(this.attributes, DefaultAttributeId.FLOOR_ID, this.uuid);
    if (synced) this.attributes = synced;
  }


  override setUUID(uuid: string) {
    super.setUUID(uuid);
    const synced = syncIdAttributeByUuid(this.attributes, DefaultAttributeId.FLOOR_ID, this.uuid);
    if (synced) this.attributes = synced;
  }

  getTiles(): ITile | undefined {
    return this.tiles;
  }

  setTiles(tiles: ITile | undefined): void {
    this.tiles = tiles;
  }

  deleteTileById(uuid: string): boolean {
    if (!this.tiles || this.tiles.uuid !== uuid) return false;

    this.tiles = undefined;
    return true;
  }

  getTexture(): ITexture | null | undefined {
    return this.texture;
  }

  setTexture(texture: ITexture | null | undefined): void {
    this.texture = texture;
  }

  setFloorShape(shape: THREE.Shape): void {
    this.floorShape = shape;
  }

  resetFloor() {
    this.tiles = undefined;
    this.texture = DEFAULT_EMPTY_TEXTURE;
    this.floorShape = undefined;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      tiles: this.tiles?.toJSON(),
      texture: serializeTexture(this.texture),
    };
  }
}

export default FloorModel;
